import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'

interface Props {
  children: ReactNode
  /** Optional label to identify which region failed (e.g. "attendee app"). */
  label?: string
  /** Optional custom fallback renderer. */
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface State {
  error: Error | null
}

/**
 * Catches render/runtime errors in its subtree and shows a readable fallback
 * instead of unmounting the whole app to a blank white screen.
 *
 * Without this, any unhandled error in a page/component collapses the entire
 * SPA to blank — which is exactly how a mis-configured draft event preview or a
 * bad block could present. In development the error message + stack are shown to
 * make the underlying failure diagnosable; in production a friendly message is
 * shown and the error is logged to the console.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface the error for diagnosis (and any future error-reporting hook).
    console.error(
      `[ErrorBoundary${this.props.label ? ` ${this.props.label}` : ''}]`,
      error,
      info.componentStack,
    )
  }

  reset = (): void => this.setState({ error: null })

  render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    if (this.props.fallback) return this.props.fallback(error, this.reset)

    const isDev = import.meta.env.DEV

    return (
      <Box
        sx={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Stack spacing={2} alignItems="center" sx={{ maxWidth: 560, textAlign: 'center' }}>
          <ErrorOutlineRoundedIcon color="error" sx={{ fontSize: 48 }} />
          <Typography variant="h6" fontWeight={700}>
            Something went wrong
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This part of the app hit an unexpected error. Try again, or reload the
            page. If it keeps happening, let the event organizer know.
          </Typography>

          {isDev && (
            <Box
              component="pre"
              sx={{
                textAlign: 'left',
                width: '100%',
                maxHeight: 240,
                overflow: 'auto',
                bgcolor: 'action.hover',
                borderRadius: 1,
                p: 1.5,
                fontSize: '0.75rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {error.message}
              {error.stack ? `\n\n${error.stack}` : ''}
            </Box>
          )}

          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={this.reset}>
              Try again
            </Button>
            <Button variant="outlined" onClick={() => window.location.reload()}>
              Reload
            </Button>
          </Stack>
        </Stack>
      </Box>
    )
  }
}
